/* eslint-disable @typescript-eslint/no-unused-vars */
import { call, all, put, takeLeading, delay } from 'redux-saga/effects'
import { createSagas } from 'src/helpers/redux'

import * as mailService from 'src/services/mail'

import { setters } from './index'
import { actions, Actions } from './actions'
import {
  ICustomLabel,
  IEmail,
  IMailAccount,
  InternalLabels,
  IThread,
  PrivateValues,
} from './types'
import { IResponse } from 'src/services/mail/http'
import { notify } from 'src/components/ui/notify'
import { realtimeService } from 'src/services/realtime'
import store from 'src/store'

export default createSagas<Actions>(actions, {
  setNewThread: function* ({ payload }) {
    yield put(setters.setNewThread(payload))
  },
  deleteLabel: function* ({ payload }) {
    yield call(mailService.deleteCustomLabel, payload)

    yield put(setters.setDeletedLabel(payload))
  },

  updateLabel: function* ({ payload }) {
    yield call(mailService.updateCustomLabel, payload)

    yield put(setters.setUpdatedLabel(payload))
  },
  createCustomLabel: function* ({ payload }) {
    const newLabel: IResponse<ICustomLabel> = yield call(
      mailService.createCustomLabel,
      payload
    )
    yield put(setters.setCreatedLabel(newLabel.data))
  },

  changeThreadPrivateMode: {
    effect: 'latest',
    saga: function* ({ payload }) {
      yield delay(500)
      yield call(mailService.changeThreadPrivateMode, payload)

      yield put(setters.changeThreadPrivacyMode(payload))
    },
  },

  getAssignLink: function* ({ payload }) {
    const authLink: IResponse<mailService.IAssignLink> = yield call(
      mailService.getAssignLink,
      payload
    )

    yield put(setters.setAssignLink(authLink.data.link))
  },

  getThread: function* ({ payload }) {
    const { thread } = payload
    const threadData: IResponse<IThread> = yield call(mailService.getThread, {
      thread,
    })

    yield put(setters.setThreadMessages(threadData.data))
  },

  sentEmail: function* ({ payload }) {
    const response: IResponse<IThread> = yield call(
      mailService.sendEmail,
      payload
    )

    if (payload.emailId) {
      yield put(setters.setSentReply(response.data))
    }

    notify({
      type: 'success',
      description: 'Email was sent successfully',
      title: 'Email sending',
    })
  },

  getCustomLabels: function* ({ payload }) {
    const labels: IResponse<ICustomLabel[]> = yield call(
      mailService.getCustomLabels,
      payload
    )

    yield put(
      setters.setCustomLabels({
        data: labels.data,
        nextCursor: labels.meta.nextCursor,
      })
    )
  },

  getMoreCustomLabels: function* ({ payload }) {
    const labels: IResponse<ICustomLabel[]> = yield call(
      mailService.getCustomLabels,
      payload
    )

    yield put(setters.mergeLabels(labels))
  },

  unlinkEmail: {
    effect: 'every',
    saga: function* ({ payload }) {
      yield call(mailService.unlinkEmail, payload)

      yield put(setters.setUnlinkedMailAccount(payload.accountId))

      notify({
        type: 'success',
        title: 'Email unlinking',
        description: 'Email was unlinked successfully',
      })
    },
  },

  toggleMailAccountPrivacy: function* ({ payload }) {
    const isPublic = payload.type === PrivateValues.PUBLIC

    if (isPublic) {
      yield call(mailService.accountMakePrivate, { accountId: payload.id })
    } else {
      yield call(mailService.accountMakePublic, { accountId: payload.id })
    }

    yield put(setters.toggleMailAccountPrivacyMode(payload.id))
  },

  getUserEmailsAccounts: function* ({ payload }) {
    const mailAccounts: IResponse<IMailAccount[]> = yield call(
      mailService.getMailAccounts,
      payload
    )

    yield put(setters.setUserMailAccounts(mailAccounts.data))
  },
  getPartnerEmailsAccounts: function* ({ payload }) {
    const mailAccounts: IResponse<IMailAccount[]> = yield call(
      mailService.getMailAccounts,
      payload
    )

    realtimeService.only<IMailAccount>(
      {
        key: actions.getPartnerEmailsAccounts.pending.toString() + '/activated',
        channel: 'mail:accounts',
        event: 'activated',
      },
      (data) => {
        store.dispatch(setters.setPartnerMailAccount(data))
      }
    )

    yield put(setters.setPartnerMailAccounts(mailAccounts.data))
  },

  toggleLabels: {
    effect: 'every',
    saga: function* ({ payload }) {
      const result = payload.threads.reduce<{
        add: string[]
        del: string[]
      }>(
        (acc, cur) => {
          const alreadyHaveLabel = cur.labels.find(
            (label) => label.code === payload.labelsCodes
          )

          if (alreadyHaveLabel) {
            acc = { ...acc, del: [...acc.del, cur.id] }
          } else {
            acc = { ...acc, add: [...acc.add, cur.id] }
          }

          return acc
        },
        { add: [], del: [] }
      )

      const {
        operation,
        threadsIds,
      }: { operation: 'add' | 'del'; threadsIds: string[] } = {
        operation: payload.operation
          ? (payload.operation as 'add' | 'del')
          : result.add.length
          ? 'add'
          : 'del',
        threadsIds: payload.operation
          ? payload.threads.map((t) => t.id)
          : result.add.length
          ? result.add
          : result.del,
      }

      yield put(
        setters.setToggledLabelThreads({
          operation,
          threadsIds,
          labelsCodes: payload.labelsCodes,
          isInternal: payload.isInternal,
        })
      )

      if (
        operation === 'del' &&
        payload.labelsCodes === InternalLabels.TRASH &&
        !payload.recover
      ) {
        yield call(mailService.deleteThreads, { threadsIds })
      } else {
        yield call(mailService.postToggleThreadLabel, {
          operation,
          threadsIds,
          labelsCodes: payload.labelsCodes,
        })
      }
    },
  },

  getThreads: {
    effect: 'latest',
    saga: function* ({ payload }) {
      yield delay(200)
      const threads: IResponse<IThread[]> = yield call(
        mailService.getThreads,
        payload
      )

      yield put(setters.setThreads(threads))
    },
  },
})
